export class InvariantViolationError extends Error {}

/**
 * Asserts that a value is truthy. If the value is falsy, an error is thrown.
 *
 * An invariant is a logical assertion that remains true throughout a specific sequence of operations.
 *
 * @example Preventing null values
 * const value: string | null = null;
 * invariant(value, 'Value should not be null'); // This will throw an error
 * value.substring(); // This code will not be executed will throw an error
 *
 * @example Type narrowing
 * const value: string | null = 'Hello world;
 * invariant(value, 'Value should not be null'); // The type of value is automatically narrowed to `string` by TS
 * value.substring(); // This code will execute without throwing an error, at compile time or runtime 🤘
 *
 * @example Using a custom check
 * invariant(a !== b, 'a and b should not be equal'); // This will throw an error if a and b are equal
 *
 * @param value The value to assert
 * @param errorMessage Optional error message to throw if the value is falsy
 */
export function invariant(value: unknown, errorMessage?: string): asserts value {
  if (!value) {
    throw new InvariantViolationError(errorMessage ?? `Expected ${value} to be truthy`);
  }
}
